import { NavHistoryObject } from '@dorian/creation-tools-ui';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { BranchStep, isMemoryTechnicalStepType } from '../../../dorian-shared/types/branch/BranchStep';
import { CursorPosition, computeForwardFallback } from '../../../contexts/Cursor/cursorReducer';
import { ChoiceMemory } from '../../../services/choiceMemory/ChoiceMemory';
import { IAchievementGetDTO } from '../../../dorian-shared/types/achievement/Achievement';
import { onGoForwardStack } from './useChoiceMemoryCallbacks/onGoForwardStack';
import { onGoBackStack } from './useChoiceMemoryCallbacks/onGoBackStack';

export interface ResolveForwardStepDeps {
  choiceMemory: ChoiceMemory | undefined;
  popupComponent: (text: string, buttonText: string, iconId: number) => Promise<void>;
  episodeId: number;
  // Yes/No prompt for PlayerHas checks; achievements supply reward display names.
  confirmPlayerHas?: (text: string) => Promise<boolean>;
  achievements?: IAchievementGetDTO[];
}

export type ForwardResolution =
  | { type: 'navigate'; branchId: number }
  | { type: 'forward'; fallback: CursorPosition | null }
  | { type: 'stop' };

function nodeHasSteps(n: Branch): n is Branch & { steps: BranchStep[] } {
  return Array.isArray(n.steps) && n.steps.length > 0;
}

function isTechnicalStep(step: BranchStep | undefined): boolean {
  return step !== undefined && isMemoryTechnicalStepType(step.stepTypeId);
}

export async function resolveForwardStep(
  node: Branch,
  cursor: CursorPosition,
  deps: ResolveForwardStepDeps,
): Promise<ForwardResolution> {
  const step = nodeHasSteps(node) && cursor.stepIndex !== null
    ? node.steps[cursor.stepIndex]
    : undefined;

  if (!isTechnicalStep(step)) {
    const fallback = nodeHasSteps(node) ? computeForwardFallback(node, cursor) : null;
    return { type: 'forward', fallback };
  }

  const result = await onGoForwardStack(
    deps.choiceMemory,
    deps.episodeId,
    step,
    cursor.stepIndex !== null ? cursor.stepIndex + 1 : 1,
    deps.popupComponent,
    deps.confirmPlayerHas,
    deps.achievements ?? [],
  );

  if (typeof result === 'number') {
    return { type: 'navigate', branchId: result };
  }
  if (result === 'goAhead') {
    const fallback = nodeHasSteps(node) ? computeForwardFallback(node, cursor) : null;
    return { type: 'forward', fallback };
  }
  // 'goBack' is never returned by onGoForwardStack — defensive fallback for undefined
  return { type: 'stop' };
}

export function resolveBackStep(
  targetNode: Branch,
  cursor: CursorPosition,
  deps: ResolveForwardStepDeps,
): 'goBack' | undefined {
  const targetStep = nodeHasSteps(targetNode) && cursor.stepIndex !== null
    ? targetNode.steps[cursor.stepIndex]
    : undefined;

  if (!isTechnicalStep(targetStep)) {
    return undefined;
  }

  // R1 limitation: isSameStep comparison in onGoBackStack may not fire correctly
  // for non-linear navigation (user must press PREV again to continue back chain).
  const historyItem: NavHistoryObject = {
    branchId: cursor.branchId,
    stepNum: cursor.stepIndex !== null ? cursor.stepIndex + 1 : 1,
    episodeId: deps.episodeId,
    episodeUuid: '',
  };
  return onGoBackStack(deps.choiceMemory, historyItem, true) === 'goBack' ? 'goBack' : undefined;
}
